import { useState } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useMintPlayLinkMutation,
  usePlayAttributionQuery,
  usePlayWhatsappQuery,
  usePlayLinksQuery,
} from '@/client/api/marketing/attribution';
import { getApiErrorMessage } from '@/client/utils/errors';

const PLATFORMS = ['instagram', 'tiktok', 'facebook', 'x', 'youtube', 'whatsapp', 'linkedin', 'snapchat', 'other'];

const inputClass = 'w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm';

function Stat({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-lg font-semibold tabular-nums text-gray-900">{value ?? '—'}</div>
    </div>
  );
}

/**
 * Tracked links for one campaign, one per creator or placement, and what they
 * produced. The creator's handle goes in utm_content, which is what groups the
 * numbers per creator.
 */
export function PlayLinksDialog({
  playId,
  playName,
  open,
  onOpenChange,
}: {
  playId: number;
  playName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const links = usePlayLinksQuery(playId, open);
  const attribution = usePlayAttributionQuery(playId, open);
  const whatsapp = usePlayWhatsappQuery(playId, open);
  const mint = useMintPlayLinkMutation(playId);
  const [form, setForm] = useState({
    destination_url: 'https://feelyfeely.com/',
    utm_content: '',
    utm_source: 'instagram',
  });

  const create = () => {
    const handle = form.utm_content.trim().replace(/^@/, '');
    if (!handle) {
      toast.error('Add the creator or placement name');
      return;
    }
    mint.mutate(
      {
        destination_url: form.destination_url.trim(),
        utm_source: form.utm_source,
        utm_medium: 'creator',
        utm_campaign: playName,
        utm_content: handle,
      },
      {
        onSuccess: (res) => {
          const url = res?.data?.url;
          if (url) void navigator.clipboard?.writeText(url).catch(() => undefined);
          toast.success(url ? `Link created and copied: ${url}` : 'Link created');
          setForm((f) => ({ ...f, utm_content: '' }));
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the link')),
      },
    );
  };

  const a = attribution.data;
  const rows = links.data ?? [];
  const wa = whatsapp.data?.totals;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Links &amp; results — {playName}</DialogTitle>
          <DialogDescription>
            Give each creator their own link, then judge them on what it produced, not on
            follower counts.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Stat label="Clicks" value={a?.clicks} />
          <Stat label="Known users" value={a?.users} />
          <Stat label="Signups" value={a?.outcomes?.signups} />
          <Stat label="Listings" value={a?.outcomes?.listings} />
          <Stat label="Paid orders" value={a?.outcomes?.orders} />
        </div>
        {a?.error && <p className="text-xs text-red-600">Results unavailable: {a.error}</p>}
        {a?.note && <p className="text-xs text-gray-500">{a.note}</p>}

        <div className="rounded-lg border border-gray-200 p-3">
          <div className="mb-2 text-sm font-medium text-gray-900">New tracked link</div>
          <div className="grid gap-2 sm:grid-cols-[1fr_140px]">
            <input
              className={inputClass}
              placeholder="Creator or placement, e.g. @bosco_tech"
              value={form.utm_content}
              onChange={(e) => setForm((f) => ({ ...f, utm_content: e.target.value }))}
            />
            <select
              className={inputClass}
              value={form.utm_source}
              onChange={(e) => setForm((f) => ({ ...f, utm_source: e.target.value }))}
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <input
            className={`${inputClass} mt-2`}
            placeholder="https://feelyfeely.com/…"
            value={form.destination_url}
            onChange={(e) => setForm((f) => ({ ...f, destination_url: e.target.value }))}
          />
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={create} disabled={mint.isPending}>
              {mint.isPending ? 'Creating…' : 'Create link'}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="py-2 pr-3 font-medium">Creator / placement</th>
                <th className="py-2 pr-3 font-medium">Platform</th>
                <th className="py-2 pr-3 text-right font-medium">Clicks</th>
                <th className="py-2 pr-3 font-medium">Link</th>
              </tr>
            </thead>
            <tbody>
              {links.isLoading && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!links.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-gray-400">
                    No tracked links yet.
                  </td>
                </tr>
              )}
              {rows.map((l) => (
                <tr key={l.link_id} className="border-b border-gray-100">
                  <td className="py-2 pr-3 text-gray-900">{l.utm_content ?? '—'}</td>
                  <td className="py-2 pr-3 text-gray-600">{l.utm_source ?? '—'}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{l.click_count}</td>
                  <td className="py-2 pr-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-mono text-xs text-gray-700 hover:text-gray-900"
                      onClick={() =>
                        void navigator.clipboard
                          ?.writeText(l.url)
                          .then(() => toast.success('Copied'))
                          .catch(() => undefined)
                      }
                      title={l.destination_url}
                    >
                      {l.url}
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">WhatsApp broadcasts</h3>
          {whatsapp.isError ? (
            <p className="text-sm text-gray-500">Could not load WhatsApp results.</p>
          ) : wa && wa.broadcasts > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                <Stat label="Broadcasts" value={wa.broadcasts} />
                <Stat label="Sent" value={wa.sent} />
                <Stat label="Replies" value={wa.replies} />
                <Stat label="Failed" value={wa.failed} />
                <Stat label="Skipped" value={wa.skipped} />
              </div>
              <p className="text-xs text-gray-500">
                A reply is a recipient who wrote back after the broadcast started. Skipped includes opt-outs.
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              {whatsapp.isLoading
                ? 'Loading...'
                : 'No WhatsApp broadcast is tagged with this campaign yet. Pick it under "Marketing campaign" when creating a broadcast.'}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
